import React, {
  useCallback, useContext, useEffect, useRef, useState
} from 'react';
import { string, bool, number, shape } from 'prop-types';
import {
  bool as boolType,
  extend,
  params,
  QueryContext,
  shape as shapeType,
  string as stringType,
} from '@thd-nucleus/data-sources';
import {
  ExperienceContext,
  useStore,
  useConfigService
} from '@thd-nucleus/experience-context';
import { BrowseSearchRedirector, DynamicComponent } from '@thd-nucleus/app-render';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { CategoryCardsDesktop } from '@thd-olt-component-react/category-cards';
import { Col, Row } from '@thd-olt-component-react/core-ui';
import {
  EmtQuestionsAndAnswers,
  PlpCategoryIntroduction
} from '@thd-olt-component-react/emt-generated-content';
import { HdhomeSignup } from '@thd-olt-component-react/email-signup';
import { declareContexts, Hydrator } from '@thd-olt-component-react/hydrator';
import {
  useHybridPLP,
  HybridContentfulWidgetComposable
} from '@thd-olt-component-react/hybrid';
import { useHelmet } from '@thd-nucleus/thd-helmet';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import {
  LoadingPlaceholder
} from '@thd-olt-component-react/loading-placeholder';
import { BrowseSearchMetadata } from '@thd-olt-component-react/metadata';
import { NoResultsFound } from '@thd-olt-component-react/no-results-found';
import {
  parseUrl,
  ProductResults,
  ProductResultsContext,
  productResultsDataModel,
  ResultsAppliedComposable,
  ResultsDimensionsComposable,
  ResultsFilterAndSortDrawer,
  PLPResultsHeader,
  ResultsPaginationComposable,
  ResultsSortByComposable,
  storeLifecycleEvent,
  useProductResults
} from '@thd-olt-component-react/product-results';
import { DefaultGridComposable } from '@thd-olt-component-react/plp-grid';
import { RelatedProducts, RelatedSearch } from '@thd-olt-component-react/related-search';
import {
  DiscoveryZonesWrapper,
  PLPRecsComposable,
  PlpRvRenderPlaceholder
} from '@thd-olt-component-react/thd-recs-containers';
import { useNavigate, useLocation } from '@thd-olt-component-react/router';
import { SearchFeedback } from '@thd-olt-component-react/search-feedback';
import {
  ProductCategoryVisnavComposable
} from '@thd-olt-component-react/product-category-visnav';
import { useThdCustomer, CustomerContext } from '@thd-olt-functional/customer-information';
import {
  SponsoredBannerComposable
} from '@thd-olt-component-react/sponsored-content';
import {
  RetailMediaCarouselFusionComposable
} from '@thd-olt-component-react/retail-media-pla-sponsored-placements';
import { OrderSample, usePLPSamples } from '@thd-olt-component-react/order-sample';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import {
  EmtGeneratedContent,
  EmtGeneratedContentLoad,
  EmtGeneratedContentModel,
  EmtLinks,
  EmtLinksLoad,
  EmtLinksModel
} from './dynamic-imports';
import {
  imagePreloader,
  isRefinedPage,
  isNIncluded
} from './browse-and-search-utils';
import { hasInStoreFilter } from '../../utils';
import { browseCampaignId, subscriptionType, requestKey, CHECK_CAT_STYLE } from '../../constants';
import '../../styles/browse-search.scss';

export const ExpandedBrowseAndSearch = (props) => {
  declareContexts([ExperienceContext, QueryContext, ProductResultsContext, CustomerContext]);
  const HYDRATION_STANDARD_SCROLL_BUFFER = 1500;

  const navigate = useNavigate();
  const ctx = useContext(ExperienceContext);
  const {
    clientStore, cookie, deliveryZip: serverDeliveryZip, customer
  } = ctx;
  const { search: querystring } = useLocation();
  const {
    pageType,
    productPodHoverDelay,
    pathname,
    customerType,
    instance
  } = props;
  const isSearch = pageType === 'search';
  const isBrowse = pageType === 'browse';
  const path = `${pathname}${querystring}`;
  const disableSSR = typeof window !== 'undefined' && window.DISABLE_SSR;
  const varnish = !disableSSR && !isSearch;
  const store = useStore({ varnish, online: false });
  const isNoResultsFoundRef = useRef(false);
  const SERVER_SIDE_PRODUCT_POD_COUNT = isSearch ? 24 : 12;
  const CLIENT_SIDE_PRODUCT_POD_COUNT = 24;
  const searchNavPropsRef = useRef({
    askForSponsored: isSearch,
    deliveryZip: serverDeliveryZip,
    pageSize: disableSSR ? CLIENT_SIDE_PRODUCT_POD_COUNT : SERVER_SIDE_PRODUCT_POD_COUNT
  });

  const viewAllFilterButton = useConfigService('fs-prop:isViewAllFilterButtonEnabled');
  const viewPrimaryFilters = useConfigService('fs-prop:isviewPrimaryFiltersEnabled');
  const isFulfillmentRedesignEnabled = useConfigService('fs-prop:isFulfillmentRedesignEnabled');
  const isGetItFastOnPlp = useConfigService('fs-prop:isGetItFastOnPlp');

  const shouldRenderPlaceholders = !instance?.thdSEOBotDetection;
  const [shouldShowInitialPlaceholderPods, setShouldShowInitialPlaceholderPods] = useState(shouldRenderPlaceholders);

  let THDCustomer = {};
  const { isCustomerIdentified = false } = useThdCustomer();

  const theme = useTheme(ExpandedBrowseAndSearch);
  const { hideFFMFilters, hideEmailPromo, hideFeedback } = theme.props;

  if (clientStore) {
    let deliveryZipCode;
    if (
      typeof window !== 'undefined'
      && window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails
    ) {
      deliveryZipCode = window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails();
    }
    searchNavPropsRef.current = {
      pageSize: CLIENT_SIDE_PRODUCT_POD_COUNT,
      askForSponsored: true,
      deliveryZip: deliveryZipCode?.zipcode
    };
    THDCustomer = typeof window !== 'undefined' ? window.THDCustomer?.default : {};
  }

  const { isClientResolved } = useContext(QueryContext);

  const onStoreChange = ({ output }) => {
    const { storeId: updatedStoreId } = output || {};
    if (!updatedStoreId) return;

    if (window && window.THD_LOCALIZER_AUTO_INIT && window.THD_LOCALIZER_AUTO_INIT.Localizer) {
      window.THD_LOCALIZER_AUTO_INIT.Localizer.localize({ storeId: updatedStoreId }).then(() => {
        window.location.reload();
      });
    }
  };

  useEffect(() => {
    window.LIFE_CYCLE_EVENT_BUS.on('product-results.change-store', onStoreChange);
    window.LIFE_CYCLE_EVENT_BUS.trigger('header.header-positioning-layout', { position: 'sticky' });
  }, []);

  const onDimensionsChange = useCallback(({ refinement }) => {
    if (!viewPrimaryFilters) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('filters-refinements.click', refinement);
    } else if (refinement?.dimensionName === 'Get It Fast') {
      storeLifecycleEvent('filters-refinements-visualNav.click', refinement);
    }
    navigate(refinement.url);
  });

  const cleanPath = path.replace(/^\/render[^/]*/, '');

  const {
    keyword,
    lowerbound,
    Nao = 0,
    nValue,
    sortby,
    sortorder,
    skipSpellCheck,
    upperbound
  } = parseUrl({ path: cleanPath });

  // For hybrid-contentful-plp
  const shouldSkipHybridPlp = isSearch || isRefinedPage(pathname);
  const hybridParams = {
    slug: isNIncluded(nValue) ? nValue : 'N-' + nValue,
    customerType,
    instance,
    isShowProducts: CHECK_CAT_STYLE.test(cleanPath),
    storeId: store.storeId,
    skip: shouldSkipHybridPlp,
    lazy: shouldSkipHybridPlp
  };
  const { data: hybridData } = useHybridPLP(hybridParams);
  useHelmet('hd-home', { hybridData }, imagePreloader, [hybridData]);
  const layouts = hybridData?.layouts;
  const layoutID = layouts?.id;
  const isHybridLayout = layoutID && layouts?.content?.__typename === 'HybridPLPLayout';
  const loadedStore = useRef(store);
  const localizedAndClientLoaded = useRef(false);

  const additionalSearchParams = {};
  const hasSponsoredSorts = !sortby || ['bestmatch', 'topsellers'].indexOf(sortby) > -1;

  if (cookie?.adobeCookie?.MCMID && searchNavPropsRef.current?.askForSponsored && hasSponsoredSorts) {
    additionalSearchParams.sponsored = true;
    additionalSearchParams.mcvisId = cookie.adobeCookie.MCMID;
  }

  const showProducts = CHECK_CAT_STYLE.test(cleanPath);
  if (showProducts) {
    additionalSearchParams.plp = true;
  }

  if (skipSpellCheck) {
    additionalSearchParams.skipSpellCheck = skipSpellCheck;
  }

  if (searchNavPropsRef.current?.deliveryZip) {
    additionalSearchParams.deliveryZip = searchNavPropsRef.current.deliveryZip;
  }

  // only populated for non-cacheable pages (e.g search)
  if (customer?.svocId) {
    additionalSearchParams.svocID = customer?.svocId;
  }

  if (Object.keys(THDCustomer).length !== 0 && THDCustomer.svocID) {
    additionalSearchParams.svocID = THDCustomer.svocID;
  }

  const response = useProductResults({
    additionalSearchParams,
    keyword,
    lowerbound,
    nValue,
    pageSize: searchNavPropsRef.current?.pageSize,
    start: Nao,
    storeId: store.storeId,
    sortby,
    sortorder,
    upperbound,
    isCustomerIdentified: store.isLocalized ? isCustomerIdentified : false,
    shouldUpdateCustomContext: false
  });

  const {
    data, error, loading, variables, responseLoading
  } = response;

  const cleanedPath = (pathname || '').replace(/render-\w+\//, '');
  // check for when search doesn't change storeId response
  // it decides not to honor storeId change of localized store
  // because 8119 store return no results so its not gonna fetch again
  // however we need to update the storeId on this page so other components
  // that need localized store (RelatedSearch) can ask for it.
  if (!loading && store.storeId !== '8119' && variables.storeId === '8119') {
    localizedAndClientLoaded.current = true;
    loadedStore.current = {
      storeId: store.storeId,
      zipCode: variables.zipCode,
    };
  } else if (data && !loading && !response?.responseLoading && store.isLocalized) {
    localizedAndClientLoaded.current = true;
    loadedStore.current = {
      storeId: store.storeId,
      zipCode: store.zipCode
    };
  }

  const { products = [], metadata } = data?.searchModel || {};
  const { contentType, stores } = metadata || {};
  const isCategory = contentType === 'categorypage';

  const stringifyItemIds = (hookData) => {
    return hookData?.searchModel?.products?.map(({ itemId: sku }) => sku).join('-');
  };

  const stringifyAppliedRefinements = (hookData) => {
    const appliedDimensions = hookData?.searchModel?.appliedDimensions || [];
    return appliedDimensions
      .map(({ refinements = [] }) => {
        return refinements.map((refinement) => refinement.refinementKey).join('-');
      })
      .join('-');
  };

  const isClientSearchModelResolved = isClientResolved({ queryName: 'searchModel' });

  useEffect(() => {
    const showPlaceholders = shouldRenderPlaceholders ? (loading || responseLoading)
      : (!products?.length && loading);

    if (showPlaceholders !== shouldShowInitialPlaceholderPods) {
      setShouldShowInitialPlaceholderPods(showPlaceholders);
    }
  }, [loading, responseLoading]);

  useEffect(() => {
    if (data && !loading && isClientSearchModelResolved) {
      const { stores: storeInfo, productCount = {} } = data?.searchModel?.metadata || {};

      const receivedAllProducts = store.storeId !== '8119' && !!storeInfo;
      const hasNoProducts = productCount?.allProducts === '0';
      const shouldTriggerBrowseAndCategoryAnalytics = isCategory
        || (pageType === 'browse' && (receivedAllProducts || hasNoProducts));

      const analyticsSearchModel = {
        ...data?.searchModel,
        defaultPageSize: searchNavPropsRef.current.pageSize,
        pageID: isNIncluded(nValue) ? nValue : 'N-' + nValue,
        isHybridContentful: isHybridLayout
      };
      if (shouldTriggerBrowseAndCategoryAnalytics) {
        LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('browse.new-products', analyticsSearchModel);
      } else if (pageType === 'search') {
        LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('search.new-products', analyticsSearchModel);
      }
    }
  }, [
    stringifyItemIds(data),
    stores?.storeId,
    stringifyAppliedRefinements(data),
    isClientSearchModelResolved
  ]);

  const decideFullWidth = isGetItFastOnPlp ? { xs: '12', sm: '12', md: '12', lg: '12' }
    : { xs: '8', sm: '9', md: '10', lg: '10' };

  const [resultsColProps, setResultsColProps] = useState(decideFullWidth);

  if (typeof window !== 'undefined') {
    window.LIFE_CYCLE_EVENT_BUS.on('LAYOUT_MANAGER.ADD_FS', (evt) => {
      if (evt.output['fs-disable:mf-dimensions__mf-results-dimensions'] === 'true') {
        setResultsColProps((prev) => {
          return { xs: '12' };
        });
      }
      if (evt.output['fs-disable:mf-dimensions__mf-results-dimensions'] === 'false') {
        setResultsColProps({
          xs: '8',
          sm: '9',
          md: '10',
          lg: '10'
        });
      }
    });
  }

  isNoResultsFoundRef.current = pageType === 'search'
    && data?.searchModel?.searchReport?.totalProducts === 0 && !!keyword;

  useEffect(() => {
    if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS) {
      window.LIFE_CYCLE_EVENT_BUS.on('fusion-navigate.change', ({ output }) => {
        const {
          path: evtPath,
          disableScroll = false,
          replace = false,
          bypassExperienceCheck = false
        } = output;

        if (evtPath) {
          navigate(evtPath, {
            replace,
            state: {
              disableScroll,
              bypassExperienceCheck
            }
          });
        }
      });
    }
  }, []);

  if (isNoResultsFoundRef.current) {
    return (
      <NoResultsFound
        keyword={keyword}
        requestKey={requestKey}
        searchData={data}
        searchError={error}
        searchLoading={loading}
        storeId={loadedStore.current?.storeId}
        isCategory={isCategory}
        pageType={pageType}
      />
    );
  }

  return (
    <>
      <BrowseSearchRedirector />
      <Row className="isBound browse-search">
        <ErrorBoundary name="browse-and-search-metadata">
          <BrowseSearchMetadata />
        </ErrorBoundary>
        <Row className="u--paddingTop u--paddingBottom">
          <ErrorBoundary name="breadcrumbs">
            <Col flatten>
              <Breadcrumbs />
            </Col>
          </ErrorBoundary>
        </Row>
        <Row>
          <ErrorBoundary name="product-results-group">
            <Col flatten>
              <ProductResults data={data} error={error} loading={loading} responseLoading={responseLoading}>
                <ErrorBoundary name="sponsored-banner-nucleus">
                  <Row>
                    <Col>
                      <SponsoredBannerComposable />
                    </Col>
                  </Row>
                </ErrorBoundary>
                <Row>
                  <ErrorBoundary name="results-header">
                    <Col flatten>
                      <div className={isHybridLayout && 'sui-ml-2'}>
                        <PLPResultsHeader />
                      </div>
                    </Col>
                  </ErrorBoundary>
                </Row>
                <Row>
                  <ErrorBoundary name="plp-category-introduction">
                    <Col>
                      <PlpCategoryIntroduction nValue={nValue} />
                    </Col>
                  </ErrorBoundary>
                </Row>
                <ErrorBoundary name="hybrid-contentful-widgets">
                  <HybridContentfulWidgetComposable />
                </ErrorBoundary>
                <ErrorBoundary name="product-category-visnav-container">
                  <ProductCategoryVisnavComposable
                    customContainerClasses="sui-mt-2 sui-mb-5 sui-w-full"
                    customHybridClasses="sui-mt-2 sui-mb-5 sui-w-full sui-mx-2"
                  />
                </ErrorBoundary>
                <Row>
                  <ErrorBoundary name="results-applied">
                    <ErrorBoundary name="top-results-applied">
                      <div>
                        <ResultsAppliedComposable
                          propsToUse={{
                            onAppliedChange: true,
                            isFulfillmentRedesignEnabled: true
                          }}
                          renderingConditions={{
                            shouldDisplayResultsAppliedOuter: true,
                            viewPrimaryFilters: false
                          }}
                        />
                      </div>
                    </ErrorBoundary>
                    <ResultsSortByComposable
                      className="sui-flex sui-justify-end"
                      renderingConditions={{
                        shouldDisplayResultsAppliedOuter: true,
                        viewPrimaryFilters: false
                      }}
                    />
                  </ErrorBoundary>
                </Row>

                <Row>
                  <ErrorBoundary
                    name="results-dimensions"
                    className="browse-search__left-outer-padding"
                  >
                    <ErrorBoundary name="mf-results-dimensions">
                      <ResultsDimensionsComposable type="vertical" hideFFMFilters={hideFFMFilters} />
                    </ErrorBoundary>
                    <Col
                      className="browse-search__pod-col-no-padding"
                      xs={resultsColProps.xs}
                      sm={resultsColProps.sm}
                      md={resultsColProps.md}
                      lg={resultsColProps.lg}
                    >
                      <PLPRecsComposable componentName="BuyItAgain" />
                      <Row className="browse-search--no-left-margin">
                        <ErrorBoundary name="top-results-applied">
                          <Col>
                            <ResultsAppliedComposable
                              propsToUse={{
                                onAppliedChange: true,
                                viewPrimaryFilters: true,
                                isFulfillmentRedesignEnabled: true,
                                isGetItFastOnPlp: true
                              }}
                              renderingConditions={{
                                isGetItFastOnPlp: true,
                              }}
                            />
                          </Col>
                        </ErrorBoundary>
                      </Row>
                      <ErrorBoundary name="filter-and-sort-drawer">
                        {!isCategory && (
                          <ResultsFilterAndSortDrawer
                            disableStickyFilter
                            excludePickUpToday
                            hideViewAll={!viewAllFilterButton}
                          />
                        )}
                      </ErrorBoundary>
                      <>
                        <>
                          <ErrorBoundary name="results-applied">
                            <Row className="browse-search--no-left-margin sui-mt-3">
                              <ResultsDimensionsComposable type="horizontal" hideFFMFilters={hideFFMFilters} />
                              <ResultsSortByComposable
                                className="sui-flex sui-justify-end"
                                hideFFMFilters={hideFFMFilters}
                                renderingConditions={{
                                  viewPrimaryFilters: true,
                                  isGetItFastOnPlp: true,
                                }}
                              />
                            </Row>
                          </ErrorBoundary>
                          <ErrorBoundary name="top-results-applied">
                            <Col className="sui-pb-5 sui-pt-0">
                              <ResultsAppliedComposable
                                propsToUse={{
                                  onAppliedChange: true,
                                  hideResultsCount: true,
                                  isFulfillmentRedesignEnabled: true,
                                  isGetItFastOnPlp: true
                                }}
                                renderingConditions={{
                                  viewPrimaryFilters: true,
                                  areAppliedRefinementsAvailable: true
                                }}
                              />
                            </Col>
                          </ErrorBoundary>
                        </>
                        <Row className="browse-search--no-left-margin">
                          <ErrorBoundary name="results-applied">
                            <ErrorBoundary name="top-results-applied">
                              <div>
                                <ResultsAppliedComposable
                                  propsToUse={{
                                    onAppliedChange: true,
                                    isFulfillmentRedesignEnabled: true,
                                    viewPrimaryFilters: true
                                  }}
                                  renderingConditions={{
                                    isGetItFastOnPlp: false,
                                    viewPrimaryFilters: true
                                  }}
                                />
                              </div>
                            </ErrorBoundary>
                            <ResultsSortByComposable
                              hideFFMFilters={hideFFMFilters}
                              renderingConditions={{
                                isGetItFastOnPlp: false,
                                viewPrimaryFilters: true
                              }}
                            />
                          </ErrorBoundary>
                        </Row>
                      </>
                      <Row className="browse-search--no-left-margin">
                        <ErrorBoundary name="results-applied">
                          <Col flatten>
                            <div>
                              <ResultsAppliedComposable
                                propsToUse={{
                                  onAppliedChange: true,
                                  isFulfillmentRedesignEnabled: true
                                }}
                                renderingConditions={{
                                  shouldDisplayResultsAppliedInner: true
                                }}
                              />
                            </div>
                            <ResultsSortByComposable
                              hideFFMFilters={hideFFMFilters}
                              renderingConditions={{
                                shouldDisplayResultsAppliedInner: true
                              }}
                            />
                          </Col>
                        </ErrorBoundary>
                        <ErrorBoundary name="wrapped-results">
                          <DefaultGridComposable />
                        </ErrorBoundary>
                        <DynamicComponent pageType={pageType}>
                          <ErrorBoundary name="pagination">
                            <ResultsPaginationComposable />
                          </ErrorBoundary>
                          {!isCategory && !hideFeedback && (
                            <Hydrator id="search-feedback" className="sui-grid sui-w-full">
                              <div>
                                <ErrorBoundary name="search-feedback">
                                  <div className="sui-py-3 sui-pl-3">
                                    <SearchFeedback />
                                  </div>
                                </ErrorBoundary>
                              </div>
                            </Hydrator>
                          )}
                        </DynamicComponent>
                      </Row>
                    </Col>
                  </ErrorBoundary>
                </Row>
              </ProductResults>
            </Col>
          </ErrorBoundary>
        </Row>
        {!hideEmailPromo && (
          <DynamicComponent pageType={pageType}>
            <Hydrator id="email-promo" className="grid">
              <Row className="u--paddingTop u--paddingBottom">
                <ErrorBoundary name="email-promo">
                  <Col>
                    <HdhomeSignup
                      formSubmission
                      subscriptionType={subscriptionType}
                      campaignId={browseCampaignId}
                    />
                  </Col>
                </ErrorBoundary>
              </Row>
            </Hydrator>
          </DynamicComponent>
        )}
        <RetailMediaCarouselFusionComposable />

        <PLPRecsComposable componentName="PlpRecsRenderPlaceholder" />

        <PlpRvRenderPlaceholder />

        {!isSearch && (
          <>
            <DynamicComponent pageType={pageType}>
              <Hydrator
                className="grid"
                id="browse-emt-section"
                delay={1500}
                placeholder={(
                  <LoadingPlaceholder
                    color="#f5f5f5"
                    ready={false}
                    style={{ height: 750 }}
                    type="rect"
                  >
                    <div />
                  </LoadingPlaceholder>
                )}
                waitFor={[EmtGeneratedContentLoad, EmtLinksLoad]}
                scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
              >
                <Row>
                  <ErrorBoundary name="emt-product-category">
                    <Col>
                      <EmtGeneratedContent type="productCategoryContent" nopadding={false} />
                    </Col>
                  </ErrorBoundary>
                </Row>
              </Hydrator>
            </DynamicComponent>

            <ErrorBoundary name="emt-questions-and-answers">
              <EmtQuestionsAndAnswers
                dynamic={{
                  pageType
                }}
                hydrator={{
                  className: 'sui-px-2 2xl:sui-px-0 sui-my-4'
                }}
              />
            </ErrorBoundary>
          </>
        )}
        {!isSearch && (
          <DynamicComponent pageType={pageType}>
            <Hydrator
              className="grid"
              id="emt-related-categories"
              delay={1500}
              placeholder={(
                <LoadingPlaceholder
                  color="#f5f5f5"
                  ready={false}
                  style={{ height: 350 }}
                  type="rect"
                >
                  <div />
                </LoadingPlaceholder>
              )}
              waitFor={[EmtGeneratedContentLoad]}
              scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
            >
              <Col>
                <EmtGeneratedContent type="relatedCategories" nopadding={false} />
              </Col>
            </Hydrator>
          </DynamicComponent>
        )}
        <ErrorBoundary name="seo-related-search">
          <RelatedSearch
            dynamic={{
              pageType
            }}
            hydrator={{
              className: 'sui-w-full sui-px-2 2xl:sui-px-0 sui-my-4 empty:sui-m-0'
            }}
          />
        </ErrorBoundary>

        <ErrorBoundary name="seo-related-products">
          <RelatedProducts
            dynamic={{
              pageType
            }}
            hydrator={{
              className: 'sui-w-full sui-px-2 2xl:sui-px-0 sui-my-4 empty:sui-m-0'
            }}
          />
        </ErrorBoundary>
        {!isSearch && (
          <DynamicComponent pageType={pageType}>
            <Hydrator
              className="grid"
              id="browse-emt-links"
              delay={1500}
              placeholder={(
                <LoadingPlaceholder
                  color="#f5f5f5"
                  ready={false}
                  style={{ height: 300 }}
                  type="rect"
                >
                  <div />
                </LoadingPlaceholder>
              )}
              waitFor={[EmtGeneratedContentLoad, EmtLinksLoad]}
              scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
            >
              <Row className="u--paddingTop u--paddingBottom">
                <ErrorBoundary name="emt-mmg">
                  <Col nopadding>
                    <EmtLinks path={cleanedPath} />
                  </Col>
                </ErrorBoundary>
              </Row>
            </Hydrator>
          </DynamicComponent>
        )}
      </Row>
    </>
  );
};

ExpandedBrowseAndSearch.displayName = 'ExpandedBrowseAndSearchPage';

ExpandedBrowseAndSearch.themeProps = {
  hideFFMFilters: bool,
  hideEmailPromo: bool,
  hideFeedback: bool,
};

ExpandedBrowseAndSearch.defaultThemeProps = {
  hideFFMFilters: false,
  hideEmailPromo: false,
  hideFeedback: false,
};

ExpandedBrowseAndSearch.propTypes = {
  pageType: string.isRequired,
  productPodHoverDelay: number,
  pathname: string.isRequired,
  customerType: string.isRequired,
  instance: shape({}).isRequired
};

ExpandedBrowseAndSearch.defaultProps = {
  productPodHoverDelay: 500
};

ExpandedBrowseAndSearch.dataModel = extend(
  {
    searchModel: params().shape({
      metadata: shapeType({
        hasPLPBanner: boolType(),
        categoryID: stringType(),
        analytics: shapeType({
          semanticTokens: stringType(),
          dynamicLCA: stringType()
        })
      })
    })
  },
  ResultsDimensionsComposable,
  ResultsFilterAndSortDrawer,
  BrowseSearchRedirector,
  BrowseSearchMetadata,
  Breadcrumbs,
  DiscoveryZonesWrapper,
  CategoryCardsDesktop,
  ProductCategoryVisnavComposable,
  HdhomeSignup,
  EmtGeneratedContentModel,
  EmtQuestionsAndAnswers,
  EmtLinksModel,
  RelatedProducts,
  RelatedSearch,
  OrderSample,
  NoResultsFound,
  productResultsDataModel,
  PlpCategoryIntroduction,
  PLPRecsComposable,
  PlpRvRenderPlaceholder,
  SponsoredBannerComposable,
  HybridContentfulWidgetComposable.dataModel,
  DefaultGridComposable,
);